import styled, { keyframes } from 'styled-components';
import { Modal } from 'react-bootstrap';

const addAnimation = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

export const StyledModalContent = styled.div`
  background-color: #f2f2f6;
  border: 2px solid #525252;
  border-radius: 3px !important;
`;

export const StyledModalBackdrop = styled.div`
  content: "";
  background-color: transparent;
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: -1;
`;

export const StyledModal = styled(Modal)`
  pointer-events: none;
  &.modal-open {
    pointer-events: auto;
  }
`;

export const StyledModalDialog = styled.div`
  max-width: 890px;
`;

export const StyledModalHeader = styled.div`
  background-color: #323232;
  color: #f5f5f5;
  border-radius: 0px;
  border-bottom: 2px solid #525252;
  padding: 1.2rem;
  display: flex;
  flex: none;
  justify-content: space-between;
  height: 66px;
  align-items: center;
`;

const ModalBodyWrapper: React.FC = (props) => {
  return <Modal.Body {...props} />;
};

/**
 * The part below the header, and the part that scrolls.
 *
 * The overlay is capped at the viewport's height, and a step carrying a long POI type — the
 * custom-field answers run to dozens of rows — does not fit under it. Scrolling this rather
 * than the whole overlay keeps the point's name and its close button in view. The scrollbar
 * is drawn thin and dark: the browser's default is a light bar across a dark panel.
 */
export const StyledModalBody = styled(ModalBodyWrapper)`
  background-color: #2e2e2e;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-width: thin;
  scrollbar-color: #5c5c5c transparent;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 4px;
    background-color: #5c5c5c;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }
`;

export const ModalLeftSide = styled.div`
  transition: background-color 0.5s ease;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

/**
 * Header over body, as a column: the wrapper around it caps the height, and a column is what
 * hands the body whatever the header leaves, so the body can scroll within it. `min-height: 0`
 * lets it shrink below its content at all, which a flex item otherwise refuses to.
 */
export const ModalRightSide = styled.div`
  width: calc(100% - 66px);
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

export const ModalContentWrapper = styled.div`
  display: flex;
  overflow: hidden !important;
  -moz-transition: all 0.3s ease;
  -webkit-transition: all 0.3s ease;
  transition: all 0.3s ease;
`;

export const ListItem = styled.div`
  background-color: #2e2e2e;
  color: #f5f5f5;
`;

export const ModalTitle = styled.h5`
  font-size: 1.075rem;
  font-weight: bolder;
  margin-bottom: 0;
  line-height: 1.4;
`;

export const NodeLeading = styled.div<{ $bgColor?: string }>`
  border-radius: 8px;
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 27px;
  margin: 8px;
  color: white;
  background-color: ${(props) => props.$bgColor || 'transparent'};
`;

export const ResourcesContainer = styled.div`
  padding-top: 10px;
  padding-bottom: 2px;
  padding-left: 10px;
  white-space: nowrap;
  text-align: left;
  overflow-y: hidden;
  overflow-x: auto;

  &::-webkit-scrollbar {
    height: 8px;
    width: 8px;
    background: #000;
  }
  &::-webkit-scrollbar-thumb {
    background: #ccc;
    -webkit-border-radius: 1ex;
    -webkit-box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.75);
  }
  &::-webkit-scrollbar-corner {
    background: #000;
  }
`;

export const NodeImage = styled.a`
  width: 200px;
  height: 135px;
  margin-right: 8px;
  background-repeat: no-repeat;
  background-size: cover;
  border-radius: 0.375rem;
  display: inline-block;
  text-decoration: none;
  -moz-transition: all 0.3s ease;
  -webkit-transition: all 0.3s ease;
  transition: all 0.3s ease;
  animation: ${addAnimation} 1s ease;
  &:hover {
    -moz-transform: scale(1.1);
    -webkit-transform: scale(1.1);
    transform: scale(1.1);
  }
`;

export const ImgThumbnail = styled.div`
  padding: 0.25rem;
  background-color: #fff;
  border: 1px solid var(--bs-border-color);
  border-radius: 0.375rem;
  max-width: 100%;
  height: auto;
  margin-right: 8px;
  border: none;
`;

export const ImageViewerContainer = styled.div`
  width: 100%;
  max-width: 100%;
`;

export const ImgInfo = styled.div`
  display: table;
  padding: 4px 8px 4px 8px;
  border-radius: 8px;
`;

export const CollapseButton = styled.button<{ $collapsed?: boolean }>`
  background-color: transparent;
  padding: 0;
  margin-right: 18px;
  border: none;
  -moz-transition: all 0.3s ease;
  -webkit-transition: all 0.3s ease;
  transition: all 0.3s ease;
  & i {
    -moz-transition: all 0.3s ease;
    -webkit-transition: all 0.3s ease;
    transition: all 0.3s ease;
    font-size: 30px !important;
    fill: #f5f5f5;
    transform:  ${(props) => props.$collapsed ? 'rotate(180deg)' : ''};
    transition: transform 0.3s ease;
  }
`;

export const CloseButton = styled.button`
  padding: 0;
  background: transparent;
  border: none;
  & i { font-size: 30px; fill: #f5f5f5 }
`;

export const ModalIcon = styled.i`
  margin-right: 18px;
`;

export const InfoRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  color: #f5f5f5;
`;

export const IconWrapper = styled.div`
  font-size: 25px;
  margin-right: 18px;
  color: #9e9e9e;
`;

export const InfoContent = styled.div`
  display: flex;
  flex-direction: column;
`;

export const InfoLabel = styled.p`
  margin-bottom: 0;
  color: #9e9e9e;
`;

export const InfoValue = styled.p`
  margin-bottom: 0;
`;

/** The custom-field block runs to several lines, so its icon sits at the top, not the middle. */
export const CustomFieldsRow = styled(InfoRow)`
  align-items: flex-start;
`;

/**
 * Takes the rest of the row, and may shrink, so a long answer wraps instead of widening it.
 * Carries the label column's width, which every section's grid reads — see `CustomFieldsGrid`.
 */
export const CustomFieldsContent = styled(InfoContent)`
  --cf-label-width: 11em;
  flex: 1;
  min-width: 0;
`;

/**
 * The type's name, as the heading of the block rather than as a row label.
 *
 * The other rows put a grey label over a white value; here the labels are the fields', so the
 * type's name has to read one level up from them — white and heavier, where the field labels
 * are grey and smaller. Grey-on-grey at one size is what made the two indistinguishable. The
 * rule underneath is what marks where the block starts: the answers sit on the overlay's own
 * background, with no frame of their own.
 */
export const CustomFieldsHeading = styled.p`
  margin: 0;
  padding-bottom: 6px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  font-size: 0.95rem;
  font-weight: 600;
  line-height: 1.5;
  color: #f5f5f5;
`;

/**
 * The answers under the heading. Deliberately unframed: a bordered, tinted panel was tried
 * first and read as one box too many inside the overlay.
 */
export const CustomFieldsBody = styled.div`
  margin-top: 10px;
`;

/** One run of answers: ungrouped fields, or one group. Set apart by space alone, no rule. */
export const CustomFieldSection = styled.div`
  & + & {
    margin-top: 14px;
  }
`;

/**
 * A group's name: the one uppercase, tracked line in the block, in a lighter grey than the
 * field labels so it sits between them and the heading.
 *
 * Capitalised by the caller (`resolveLabelUpper`), not by `text-transform`: CSS cases by the
 * page's language, and the page is not in the label's — a Turkish "tipleri" came out as
 * "TIPLERI" rather than "TİPLERİ".
 */
export const CustomFieldGroupTitle = styled.p`
  margin: 0 0 6px;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: #c7c7c7;
`;

/**
 * Label and answer side by side. The label column is one fixed width for the whole block, so
 * answers line up across sections; a grouped grid is indented behind a rule that marks its
 * fields as the group's, and gives the indent back out of its label column so its answers
 * still start where the ungrouped ones do.
 */
export const CustomFieldsGrid = styled.div<{ $grouped?: boolean }>`
  --cf-indent: ${({ $grouped }) => ($grouped ? '12px' : '0px')};
  display: grid;
  grid-template-columns: minmax(0, calc(var(--cf-label-width) - var(--cf-indent))) minmax(0, 1fr);
  column-gap: 16px;
  row-gap: 6px;
  ${({ $grouped }) => $grouped && `
    padding-left: 10px;
    border-left: 2px solid rgba(255, 255, 255, 0.16);
  `}
`;

export const CustomFieldLabel = styled.span`
  font-size: 0.8125rem;
  line-height: 1.45;
  color: #9e9e9e;
`;

/** `pre-wrap` so a multiline text answer keeps the line breaks the inspector typed. */
export const CustomFieldValue = styled.span`
  font-size: 0.875rem;
  line-height: 1.4;
  color: #f5f5f5;
  overflow-wrap: anywhere;
  white-space: pre-wrap;
`;

/**
 * The step pager, pinned to the bottom of the scrolling body: on a long step it would
 * otherwise sit below everything, out of sight, and moving to the next step would first
 * mean scrolling to the end of this one. Painted with the body's colour so content scrolling
 * under it does not show through.
 */
export const PaginationWrapper = styled.div`
  position: sticky;
  bottom: 0;
  z-index: 1;
  margin-top: 0.5rem;
  padding-block: 0.5rem;
  background-color: #2e2e2e;
`;
